import React, { useState, useEffect } from 'react';
import { 
    X, Check, AlertTriangle, FileText, LayoutList, Calculator, Eye,
    ArrowRight, ArrowLeft, Save, Plus, Trash2, Loader2, Info, Database,
    AlertCircle, CheckCircle
} from 'lucide-react';
import { apiClient } from '../utils/apiClient';

export default function CustomReportWizard({ reportId, onClose }) {
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [modalConfig, setModalConfig] = useState({ isOpen: false, type: 'info', title: '', message: '', onCloseCallback: null });

    const showModal = (type, title, message, onCloseCallback = null) => {
        setModalConfig({ isOpen: true, type, title, message, onCloseCallback });
    };

    const closeModal = () => setModalConfig(prev => ({ ...prev, isOpen: false }));
    
    // Step 1: Basic Info
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    
    // Step 2: Columns
    const [availableColumns, setAvailableColumns] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [structure, setStructure] = useState('daily');
    
    // Step 3: Calculated Columns
    const [calcColumns, setCalcColumns] = useState([]);
    
    // Step 4: Preview Data
    const [previewData, setPreviewData] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                let targetBaseType = 'puantaj_raporu';
                
                // If editing, load existing report
                if (reportId) {
                    const report = await apiClient(`reports/custom/${reportId}`);
                    setName(report.name);
                    setDescription(report.description || "");
                    
                    if (report.baseReportType && report.baseReportType.endsWith('_summary')) {
                        setStructure('summary');
                        targetBaseType = 'puantaj_raporu_summary';
                    } else {
                        setStructure('daily');
                    }
                    
                    const colsRes = await apiClient(`reports/custom/available-columns/${targetBaseType}`);
                    setAvailableColumns(colsRes);
                    
                    if (report.selectedColumns) {
                        const selCols = colsRes.filter(c => report.selectedColumns.includes(c.key));
                        setSelectedColumns(selCols);
                    }
                    if (report.calculatedColumns) {
                        const calcCols = report.calculatedColumns.map(c => ({
                            headerName: c.resultColumnName || "",
                            leftOperand: c.formulaLeftOperand || "",
                            operator: c.formulaOperator === 'Add' || c.formulaOperator === 1 ? '+' : c.formulaOperator === 'Subtract' || c.formulaOperator === 2 ? '-' : c.formulaOperator === 'Multiply' || c.formulaOperator === 3 ? '*' : '/',
                            rightOperandType: c.formulaRightConstant != null ? 'Number' : 'Column',
                            rightOperandValue: c.formulaRightOperand || "",
                            rightOperandNumber: c.formulaRightConstant || 0,
                            displayFormat: c.displayFormat || "none"
                        }));
                        setCalcColumns(calcCols);
                    }
                } else {
                    const colsRes = await apiClient('reports/custom/available-columns/puantaj_raporu');
                    setAvailableColumns(colsRes);
                    const defaultKeys = ['personnelName', 'department', 'date', 'actualDuration'];
                    const defaultCols = colsRes.filter(c => defaultKeys.includes(c.key));
                    setSelectedColumns(defaultCols);
                }
            } catch (e) {
                showModal('error', 'Hata', "Veriler yüklenirken bir hata oluştu. Lütfen bağlantınızı kontrol edip tekrar deneyin.");
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, [reportId]);

    // Handle structure change column fetching
    const handleStructureChange = async (newStructure) => {
        setStructure(newStructure);
        setLoading(true);
        try {
            const targetBaseType = newStructure === 'summary' ? 'puantaj_raporu_summary' : 'puantaj_raporu';
            const colsRes = await apiClient(`reports/custom/available-columns/${targetBaseType}`);
            setAvailableColumns(colsRes);
            setSelectedColumns(prev => prev.filter(c => colsRes.some(ac => ac.key === c.key)));
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const handleNext = () => {
        if (step === 1 && !name.trim()) {
            showModal('error', 'Uyarı', "Raporunuzu kaydedebilmek için lütfen geçerli bir rapor adı giriniz.");
            return;
        }
        if (step === 2 && selectedColumns.length === 0) {
            showModal('error', 'Uyarı', "Raporun oluşturulabilmesi için tablodan en az bir tane kolon seçmelisiniz.");
            return;
        }
        if (step === 3) {
            // Validate calculated columns
            for (let i = 0; i < calcColumns.length; i++) {
                const c = calcColumns[i];
                if (!c.headerName.trim()) {
                    showModal('error', 'Uyarı', `${i + 1}. Hesap Kolonu için geçerli bir ad girmelisiniz.`);
                    return;
                }
                if (!c.leftOperand) {
                    showModal('error', 'Uyarı', `${c.headerName} kolonu için 1. Değeri seçmelisiniz.`);
                    return;
                }
                if (c.rightOperandType === 'Column' && !c.rightOperandValue) {
                    showModal('error', 'Uyarı', `${c.headerName} kolonu için 2. Değeri seçmelisiniz.`);
                    return;
                }
            }
            generatePreview();
        }
        setStep(prev => prev + 1);
    };

    const handlePrev = () => {
        setStep(prev => prev - 1);
    };

    const mapCalcColumns = (cols) => cols.map(c => ({
        resultColumnName: c.headerName,
        formulaLeftOperand: c.leftOperand,
        formulaOperator: c.operator === '+' ? 'Add' : c.operator === '-' ? 'Subtract' : c.operator === '*' ? 'Multiply' : 'Divide',
        formulaRightOperand: c.rightOperandType === 'Column' ? c.rightOperandValue : null,
        formulaRightConstant: c.rightOperandType === 'Number' ? c.rightOperandNumber : null,
        displayFormat: c.displayFormat || "none"
    }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                name,
                description,
                baseReportType: structure === 'summary' ? "puantaj_raporu_summary" : "puantaj_raporu",
                selectedColumns: selectedColumns.map(c => c.key),
                calculatedColumns: mapCalcColumns(calcColumns)
            };
            
            if (reportId) {
                await apiClient(`reports/custom/${reportId}`, {
                    method: 'PUT',
                    body: payload
                });
                showModal('success', 'Başarılı', "Değişiklikleriniz başarıyla kaydedildi.", onClose);
            } else {
                await apiClient('reports/custom', {
                    method: 'POST',
                    body: payload
                });
                showModal('success', 'Başarılı', "Yeni özel raporunuz başarıyla oluşturuldu.", onClose);
            }
        } catch (e) {
            let errorMsg = e.message;
            if (errorMsg && errorMsg.includes('Özel rapor kotanızı')) {
                errorMsg = "Özel rapor kotanızı doldurdunuz. Lütfen kullanmadığınız raporları silin veya paketinizi yükseltin.";
            } else if (errorMsg && errorMsg.includes('yetkiniz bulunmamaktadır')) {
                errorMsg = "Bu işlemi yapmak için yetkiniz bulunmamaktadır. Lütfen paket özelliklerinizi kontrol edin.";
            } else {
                errorMsg = "Rapor kaydedilirken beklenmedik bir sistem hatası oluştu: " + errorMsg;
            }
            showModal('error', 'İşlem Başarısız', errorMsg);
        } finally {
            setSaving(false);
        }
    };

    const generatePreview = async () => {
        setPreviewLoading(true);
        try {
            const payload = {
                Config: {
                    name,
                    baseReportType: structure === 'summary' ? "puantaj_raporu_summary" : "puantaj_raporu",
                    selectedColumns: selectedColumns.map(c => c.key),
                    calculatedColumns: mapCalcColumns(calcColumns)
                },
                Filters: {
                    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString(),
                    endDate: new Date().toISOString()
                }
            };
            const res = await apiClient('reports/custom/generate-preview', {
                method: 'POST',
                body: payload
            });
            setPreviewData(res);
        } catch (e) {
            showModal('error', 'Önizleme Hatası', "Rapor önizlemesi oluşturulurken bir sorun oluştu. Lütfen formüllerinizi kontrol edin. Detay: " + e.message);
        } finally {
            setPreviewLoading(false);
        }
    };

    const toggleColumn = (col) => {
        if (selectedColumns.find(c => c.key === col.key)) {
            setSelectedColumns(selectedColumns.filter(c => c.key !== col.key));
        } else {
            setSelectedColumns([...selectedColumns, col]);
        }
    };

    // Calculate Column Builder
    const addCalcColumn = () => {
        setCalcColumns([...calcColumns, { headerName: "", leftOperand: "", operator: "+", rightOperandType: "Column", rightOperandValue: "", rightOperandNumber: 0, displayFormat: "none" }]);
    };
    
    const updateCalcColumn = (index, field, value) => {
        const newCols = [...calcColumns];
        newCols[index][field] = value;
        setCalcColumns(newCols);
    };

    const removeCalcColumn = (index) => {
        const newCols = [...calcColumns];
        newCols.splice(index, 1);
        setCalcColumns(newCols);
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden animate-slideUp">
                
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/80 dark:bg-gray-800/80 flex items-center justify-between shrink-0">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                            {reportId ? "Özel Raporu Düzenle" : "Yeni Özel Rapor Oluştur"}
                        </h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Adım {step} / 5</p>
                    </div>
                    <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 bg-white dark:bg-gray-700 rounded-full shadow-sm hover:shadow transition">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6 bg-white dark:bg-gray-800 relative">
                    {loading && (
                        <div className="absolute inset-0 z-10 bg-white/50 dark:bg-gray-800/50 flex items-center justify-center backdrop-blur-sm">
                            <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
                        </div>
                    )}
                    
                    {/* Stepper Headers */}
                    <div className="flex items-center justify-between mb-8 max-w-3xl mx-auto">
                        {['Temel Bilgiler', 'Kolonlar', 'Hesaplamalar', 'Önizleme', 'Onay'].map((s, i) => (
                            <div key={i} className={`flex flex-col items-center gap-2 ${step === i + 1 ? 'opacity-100' : 'opacity-50'}`}>
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${step >= i + 1 ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'}`}>
                                    {step > i + 1 ? <Check className="w-5 h-5" /> : i + 1}
                                </div>
                                <span className={`text-xs font-medium ${step >= i + 1 ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>{s}</span>
                            </div>
                        ))}
                    </div>

                    {/* Step 1 */}
                    {step === 1 && (
                        <div className="max-w-2xl mx-auto space-y-6 animate-fadeIn">
                            <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-xl flex gap-3 border border-blue-100 dark:border-blue-800">
                                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-blue-800 dark:text-blue-300">
                                    <p className="font-semibold mb-1">Bilgilendirme</p>
                                    <p>Bu ilk sürümde özel raporlar sadece <strong>Puantaj Özeti</strong> verisi üzerinden oluşturulmaktadır. İlerleyen güncellemelerde farklı kaynaklar da eklenecektir.</p>
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rapor Adı <span className="text-red-500">*</span></label>
                                <input 
                                    type="text" 
                                    value={name} 
                                    onChange={e => setName(e.target.value)}
                                    placeholder="Örn: Hafta Sonu Fazla Mesaileri"
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Açıklama (Opsiyonel)</label>
                                <textarea 
                                    value={description} 
                                    onChange={e => setDescription(e.target.value)}
                                    rows={3}
                                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Kaynak Rapor</label>
                                <div className="px-4 py-2 border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 rounded-lg cursor-not-allowed font-medium flex items-center">
                                    <Database className="w-4 h-4 mr-2" />
                                    Puantaj Özeti
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Rapor Yapısı <span className="text-red-500">*</span></label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <label className={`cursor-pointer border rounded-xl p-4 flex flex-col gap-1 transition-all ${structure === 'daily' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'}`}>
                                        <div className="flex items-center gap-2">
                                            <input type="radio" name="structure" value="daily" checked={structure === 'daily'} onChange={() => handleStructureChange('daily')} className="text-blue-600" />
                                            <span className={`font-semibold ${structure === 'daily' ? 'text-blue-800 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>Gün Bazlı Detay</span>
                                        </div>
                                        <span className="text-sm text-gray-500 pl-6">Personelin her günü ayrı bir satır olarak listelenir. Klasik döküm raporudur.</span>
                                    </label>
                                    <label className={`cursor-pointer border rounded-xl p-4 flex flex-col gap-1 transition-all ${structure === 'summary' ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300'}`}>
                                        <div className="flex items-center gap-2">
                                            <input type="radio" name="structure" value="summary" checked={structure === 'summary'} onChange={() => handleStructureChange('summary')} className="text-blue-600" />
                                            <span className={`font-semibold ${structure === 'summary' ? 'text-blue-800 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>Personel Bazlı Toplam</span>
                                        </div>
                                        <span className="text-sm text-gray-500 pl-6">Tarih aralığındaki veriler kişi bazında toplanır. Her personel tek satırda görünür.</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2 */}
                    {step === 2 && (
                        <div className="max-w-4xl mx-auto animate-fadeIn">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4">Görüntülenecek Kolonları Seçin</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                {availableColumns.map(col => {
                                    const selectedIndex = selectedColumns.findIndex(c => c.key === col.key);
                                    const isSelected = selectedIndex !== -1;
                                    return (
                                        <div 
                                            key={col.key}
                                            onClick={() => toggleColumn(col)}
                                            className={`p-3 rounded-xl border cursor-pointer transition-all duration-200 flex items-center justify-between ${isSelected ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 shadow-sm ring-1 ring-blue-500/50' : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'}`}
                                        >
                                            <div className="flex flex-col overflow-hidden pr-2">
                                                <span className={`font-medium truncate transition-colors ${isSelected ? 'text-blue-700 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>{col.name}</span>
                                                <span className="text-xs text-gray-400 dark:text-gray-500 font-mono truncate">{col.key}</span>
                                            </div>
                                            <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isSelected ? 'bg-blue-600 text-white shadow-md transform scale-110' : 'border-2 border-gray-200 dark:border-gray-600 bg-transparent'}`}>
                                                {isSelected && (
                                                    <span className="text-sm font-bold animate-in zoom-in spin-in-12 fade-in duration-300">
                                                        {selectedIndex + 1}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Step 3 */}
                    {step === 3 && (
                        <div className="max-w-4xl mx-auto animate-fadeIn">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100">Hesap Kolonları Ekle (Opsiyonel)</h3>
                                    <p className="text-sm text-gray-500">Mevcut kolonları kullanarak (+, -, *, /) matematiksel işlemler yapabilirsiniz.</p>
                                </div>
                                <button 
                                    onClick={addCalcColumn}
                                    className="flex items-center px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 transition text-sm font-medium"
                                >
                                    <Plus className="w-4 h-4 mr-1" /> Hesap Kolonu Ekle
                                </button>
                            </div>

                            {calcColumns.length === 0 ? (
                                <div className="text-center py-12 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl text-gray-500 dark:text-gray-400">
                                    <Calculator className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                                    Hesap kolonu tanımlanmamış. İhtiyacınız yoksa "İleri" diyerek devam edebilirsiniz.
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {calcColumns.map((c, idx) => (
                                        <div key={idx} className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col md:flex-row gap-4 items-start md:items-end">
                                            <div className="flex-1 w-full">
                                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Yeni Kolon Adı</label>
                                                <input 
                                                    type="text" 
                                                    value={c.headerName} 
                                                    onChange={e => updateCalcColumn(idx, 'headerName', e.target.value)}
                                                    placeholder="Örn: Net Mesai"
                                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white"
                                                />
                                            </div>
                                            
                                            <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
                                                {/* Left Operand */}
                                                <div className="flex-1 min-w-[150px]">
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Değer 1</label>
                                                    <select 
                                                        value={c.leftOperand}
                                                        onChange={e => updateCalcColumn(idx, 'leftOperand', e.target.value)}
                                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white"
                                                    >
                                                        <option value="">Seçiniz...</option>
                                                        {availableColumns.filter(col => col.isNumeric && col.key !== 'overTime').map(col => (
                                                            <option key={col.key} value={col.key}>{col.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                
                                                {/* Operator */}
                                                <div className="w-16">
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">İşlem</label>
                                                    <select 
                                                        value={c.operator}
                                                        onChange={e => updateCalcColumn(idx, 'operator', e.target.value)}
                                                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-bold bg-gray-100 dark:bg-gray-600 dark:text-white text-center"
                                                    >
                                                        <option value="+">+</option>
                                                        <option value="-">-</option>
                                                        <option value="*">x</option>
                                                        <option value="/">/</option>
                                                    </select>
                                                </div>

                                                {/* Right Operand Type */}
                                                <div className="w-24">
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Değer 2 Tipi</label>
                                                    <select 
                                                        value={c.rightOperandType}
                                                        onChange={e => {
                                                            updateCalcColumn(idx, 'rightOperandType', e.target.value);
                                                            if (e.target.value === 'Number') updateCalcColumn(idx, 'rightOperandValue', "");
                                                        }}
                                                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white"
                                                    >
                                                        <option value="Column">Kolon</option>
                                                        <option value="Number">Sabit Sayı</option>
                                                    </select>
                                                </div>

                                                {/* Right Operand Value */}
                                                <div className="flex-1 min-w-[150px]">
                                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Değer 2</label>
                                                    {c.rightOperandType === 'Column' ? (
                                                        <select 
                                                            value={c.rightOperandValue}
                                                            onChange={e => updateCalcColumn(idx, 'rightOperandValue', e.target.value)}
                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white"
                                                        >
                                                            <option value="">Seçiniz...</option>
                                                            {availableColumns.filter(col => col.isNumeric && col.key !== 'overTime').map(col => (
                                                                <option key={col.key} value={col.key}>{col.name}</option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input 
                                                            type="number"
                                                            step="0.01"
                                                            value={c.rightOperandNumber}
                                                            onChange={e => updateCalcColumn(idx, 'rightOperandNumber', parseFloat(e.target.value) || 0)}
                                                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white"
                                                        />
                                                    )}
                                                </div>
                                                
                                                <div className="flex items-end gap-2 w-full md:w-auto mt-2 md:mt-0">
                                                    <div className="w-40">
                                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Çıktı Formatı</label>
                                                        <select 
                                                            value={c.displayFormat || "none"}
                                                            onChange={e => updateCalcColumn(idx, 'displayFormat', e.target.value)}
                                                            className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white"
                                                        >
                                                            <option value="none">Ham Sayı (Örn: 540)</option>
                                                            <option value="minutes">Dakika (Örn: 540 dk)</option>
                                                            <option value="decimal_hours">Ondalık Saat (Örn: 9 sa)</option>
                                                            <option value="hours_minutes">Saat/Dak (Örn: 9 sa 0 dk)</option>
                                                        </select>
                                                    </div>
                                                    
                                                    <button 
                                                        onClick={() => removeCalcColumn(idx)}
                                                        className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition"
                                                        title="Kaldır"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 4 */}
                    {step === 4 && (
                        <div className="w-full h-full animate-fadeIn flex flex-col">
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-100 mb-2">Önizleme (İlk 50 Satır)</h3>
                            <p className="text-sm text-gray-500 mb-4">Seçtiğiniz kolonlar ve hesaplamalar ile raporun nasıl görüneceğini aşağıdan inceleyebilirsiniz.</p>
                            
                            {previewLoading ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                                </div>
                            ) : previewData ? (
                                <div className="flex-1 overflow-auto border border-gray-200 dark:border-gray-700 rounded-xl">
                                    <table className="w-full text-sm text-left">
                                        <thead className="text-xs text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
                                            <tr>
                                                {previewData.headers.map((h, i) => (
                                                    <th key={i} className="px-4 py-3 font-semibold whitespace-nowrap">{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                            {previewData.rows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={previewData.headers.length} className="px-4 py-8 text-center text-gray-500">
                                                        Bu tarih aralığında gösterilecek örnek veri bulunamadı.
                                                    </td>
                                                </tr>
                                            ) : (
                                                previewData.rows.map((row, idx) => (
                                                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800 bg-white dark:bg-gray-900">
                                                        {row.map((cell, cidx) => (
                                                            <td key={cidx} className="px-4 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap">{cell}</td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="flex-1 flex items-center justify-center text-gray-500">
                                    Önizleme yüklenemedi.
                                </div>
                            )}
                        </div>
                    )}

                    {/* Step 5 */}
                    {step === 5 && (
                        <div className="max-w-2xl mx-auto py-12 text-center animate-fadeIn">
                            <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Save className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                            </div>
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 mb-4">Raporu Kaydet</h2>
                            
                            <div className="bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-xl p-6 text-left mb-8">
                                <div className="flex items-start gap-4">
                                    <AlertTriangle className="w-6 h-6 text-yellow-600 dark:text-yellow-500 mt-0.5" />
                                    <div>
                                        <h4 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-2">Önemli Kurallar</h4>
                                        <ul className="list-disc list-inside text-sm text-yellow-700 dark:text-yellow-300 space-y-2">
                                            <li>Bu raporu kaydettikten sonra <strong>10 gün</strong> boyunca düzenleyebilirsiniz.</li>
                                            <li>10 gün dolduğunda rapor kilitlenir ve sadece görüntülenebilir/çalıştırılabilir.</li>
                                            {!reportId && <li>Kaydetme işlemi özel rapor kotanızdan <strong>1 hak</strong> düşecektir.</li>}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Controls */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 flex items-center justify-between shrink-0">
                    <button 
                        onClick={step === 1 ? onClose : handlePrev}
                        className="px-6 py-2 rounded-lg font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 transition flex items-center"
                    >
                        {step === 1 ? 'İptal' : <><ArrowLeft className="w-4 h-4 mr-2" /> Geri</>}
                    </button>
                    
                    {step < 5 ? (
                        <button 
                            onClick={handleNext}
                            className="px-6 py-2 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 shadow-md transition flex items-center"
                        >
                            İleri <ArrowRight className="w-4 h-4 ml-2" />
                        </button>
                    ) : (
                        <button 
                            onClick={handleSave}
                            disabled={saving}
                            className="px-8 py-2 rounded-lg font-bold text-white bg-green-600 hover:bg-green-700 shadow-lg transition flex items-center"
                        >
                            {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
                            {saving ? 'Kaydediliyor...' : 'Kaydet ve Tamamla'}
                        </button>
                    )}
                </div>
            </div>

            {/* Error/Success Modal Overlay */}
            {modalConfig.isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className={`p-4 border-b ${modalConfig.type === 'error' ? 'bg-red-50 border-red-100 dark:bg-red-900/20 dark:border-red-900/30' : 'bg-green-50 border-green-100 dark:bg-green-900/20 dark:border-green-900/30'} flex items-center gap-3`}>
                            {modalConfig.type === 'error' ? <AlertCircle className="w-6 h-6 text-red-500" /> : <CheckCircle className="w-6 h-6 text-green-500" />}
                            <h3 className={`font-semibold ${modalConfig.type === 'error' ? 'text-red-700 dark:text-red-400' : 'text-green-700 dark:text-green-400'}`}>
                                {modalConfig.title}
                            </h3>
                        </div>
                        <div className="p-5 text-gray-600 dark:text-gray-300">
                            {modalConfig.message}
                        </div>
                        <div className="p-4 bg-gray-50 dark:bg-gray-900/50 flex justify-end">
                            <button 
                                onClick={() => {
                                    closeModal();
                                    if (modalConfig.onCloseCallback) modalConfig.onCloseCallback();
                                }}
                                className="px-5 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-lg font-medium transition"
                            >
                                Tamam
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
